import React from 'react';

export const ClassSkeleton: React.FC = () => (
    <div className="animate-pulse bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden mb-6">
        <div className="p-5 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
            <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-slate-200 rounded-lg"></div>
                <div className="space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-32"></div>
                    <div className="h-3 bg-slate-200 rounded w-24"></div>
                </div>
            </div>
            <div className="flex space-x-2">
                <div className="w-20 h-8 bg-slate-200 rounded-lg"></div>
                <div className="w-8 h-8 bg-slate-200 rounded-full"></div>
            </div>
        </div>
        <div className="p-4 bg-white">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {[1, 2, 3, 4].map(i => (
                    <div key={i} className="h-16 bg-slate-50 rounded-xl border border-slate-100"></div>
                ))}
            </div>
            <div className="space-y-3">
                {[1, 2, 3].map(i => (
                    <div key={i} className="flex items-center space-x-4 p-3 border-b border-slate-50">
                        <div className="w-10 h-10 bg-slate-100 rounded-full"></div>
                        <div className="flex-1 space-y-2">
                            <div className="h-4 bg-slate-100 rounded w-1/4"></div>
                            <div className="h-3 bg-slate-100 rounded w-1/2"></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

export const LibraryItemSkeleton: React.FC = () => (
    <div className="animate-pulse flex items-center justify-between py-4 border-b border-gray-100 px-6 -mx-6">
        <div className="flex items-center space-x-4 flex-1">
            <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
            <div className="space-y-2 flex-1">
                <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                <div className="h-3 bg-gray-200 rounded w-1/4"></div>
            </div>
        </div>
        <div className="flex space-x-2">
            <div className="w-8 h-8 bg-gray-100 rounded"></div>
            <div className="w-8 h-8 bg-gray-100 rounded"></div>
            <div className="w-8 h-8 bg-gray-100 rounded"></div>
        </div>
    </div>
);

export const LibrarySkeleton: React.FC = () => (
    <div className="space-y-1">
        {[1, 2, 3, 4, 5, 6].map(i => (
            <LibraryItemSkeleton key={i} />
        ))}
    </div>
);

export const SharedResourceSkeleton: React.FC = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="animate-pulse p-4 rounded-xl border border-slate-100 bg-white shadow-sm flex items-start gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg shrink-0"></div>
                <div className="flex-1 space-y-2 min-w-0">
                    <div className="h-4 bg-slate-100 rounded w-3/4"></div>
                    <div className="h-3 bg-slate-100 rounded w-1/2"></div>
                    <div className="h-3 bg-slate-50 rounded w-1/4"></div>
                </div>
            </div>
        ))}
    </div>
);

export const TabSkeleton: React.FC = () => (
    <div className="animate-pulse flex space-x-4 border-b border-gray-200 mb-6">
        {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-10 bg-gray-200 rounded-t-lg w-24"></div>
        ))}
    </div>
);

export const InteractiveVideoSkeleton: React.FC = () => (
    <div className="animate-pulse space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
                <div className="bg-gray-200 rounded-2xl aspect-video w-full"></div>
                <div className="bg-white rounded-2xl p-6 border border-gray-100 space-y-4">
                    <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-100 rounded w-1/2"></div>
                    <div className="pt-4 border-t border-gray-50 flex items-center gap-2">
                        <div className="w-4 h-4 bg-gray-100 rounded-full"></div>
                        <div className="h-3 bg-gray-100 rounded w-1/3"></div>
                    </div>
                </div>
            </div>
            <div className="space-y-6">
                <div className="bg-white rounded-2xl p-6 border border-gray-100">
                    <div className="h-6 bg-gray-200 rounded w-1/2 mb-8"></div>
                    <div className="relative pl-4 space-y-8">
                        <div className="absolute left-[27px] top-2 bottom-2 w-0.5 bg-gray-50"></div>
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="relative pl-10 flex items-center">
                                <div className="absolute left-0 w-7 h-7 bg-gray-100 rounded-full border-2 border-white shadow-sm"></div>
                                <div className="flex-1 h-20 bg-gray-50 rounded-2xl border border-gray-100"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </div>
);

export const ChildDashboardSkeleton: React.FC = () => (
    <div className="animate-pulse space-y-8 pb-20">
        {/* Welcome Banner Skeleton */}
        <div className="relative overflow-hidden rounded-[2.5rem] bg-slate-800 p-8 text-white shadow-2xl">
            <div className="relative flex flex-row items-center gap-8">
                <div className="w-20 h-20 rounded-[2rem] bg-slate-700"></div>
                <div className="flex-1 space-y-3">
                    <div className="h-6 bg-slate-700 rounded-lg w-1/3"></div>
                    <div className="h-4 bg-slate-700 rounded-lg w-1/4"></div>
                </div>
                <div className="flex gap-3">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="w-20 h-20 bg-slate-700 rounded-3xl"></div>
                    ))}
                </div>
            </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 xl:col-span-9 space-y-8">
                {/* News & Activities Skeleton */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center px-2">
                        <div className="h-6 bg-slate-200 rounded w-48"></div>
                        <div className="h-4 bg-slate-200 rounded w-24"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-24 bg-slate-100 rounded-[1.8rem] border border-slate-200"></div>
                        ))}
                    </div>
                </div>

                {/* Classroom & Assignments Skeleton */}
                <div className="space-y-8">
                    <div className="flex justify-between items-center px-2">
                        <div className="h-6 bg-slate-200 rounded w-64"></div>
                        <div className="h-4 bg-slate-200 rounded w-32"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-64 bg-slate-100 rounded-[2.5rem] border border-slate-200"></div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right Column Skeleton */}
            <div className="lg:col-span-4 xl:col-span-3 space-y-8">
                <div className="h-[300px] bg-slate-100 rounded-[2.5rem] border border-slate-200"></div>
                <div className="h-[200px] bg-slate-100 rounded-[2.5rem] border border-slate-200"></div>
            </div>
        </div>
    </div>
);
